import * as React from 'react'
import { Link } from 'react-router-dom'
import { SottovoWordmark } from '@/components/brand'

interface AuthShellProps {
  /** フォーム見出し (h1)。 */
  title: string
  /** 見出し下のサブコピー (ブランドボイス)。省略可。 */
  subtitle?: string
  /** フォーム本体・補助リンク等。 */
  children: React.ReactNode
}

/**
 * Sottovo 認証画面の共通 2 カラムシェル。
 *
 * - lg 以上: 左=フォーム / 右=歓迎イラスト (装飾、alt 空)。
 * - モバイル: 1 カラム (フォームのみ)。イラストは `lg:flex` で隠す。
 * - ルートに `app-v2` を付与して v2 トークンスコープ (背景・フォント・focus ring) を有効化。
 * - ダークモード CSS は持たない (設計書 §3.1)。エージェント用語は露出させない (§2.2)。
 *
 * 認証ロジックは一切持たず、レイアウトとブランド提示のみを担う。
 */
export function AuthShell({ title, subtitle, children }: AuthShellProps) {
  return (
    <div className="app-v2 flex min-h-screen bg-v2-bg text-v2-ink">
      {/* 左: フォームカラム */}
      <main className="flex w-full flex-col px-6 py-10 sm:px-10 lg:w-1/2 lg:px-16">
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
          <div className="mb-8">
            <Link
              to="/"
              aria-label="Sottovo ホームへ"
              className="inline-flex rounded-v2-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
            >
              <SottovoWordmark size={28} />
            </Link>
          </div>

          <header className="mb-8">
            <h1 className="text-v2-h2 font-semibold text-v2-ink">{title}</h1>
            {subtitle ? (
              <p className="mt-2 text-base leading-relaxed text-v2-ink-muted">
                {subtitle}
              </p>
            ) : null}
          </header>

          {children}
        </div>
      </main>

      {/* 右: 装飾イラスト (lg 以上のみ)。alt 空・aria-hidden で支援技術から隠す。 */}
      <aside
        aria-hidden="true"
        className="relative hidden w-1/2 items-center justify-center overflow-hidden bg-v2-primary-soft lg:flex"
      >
        <img
          src="/v2/illustrations/auth-welcome.png"
          alt=""
          className="h-full w-full object-cover"
          loading="eager"
          decoding="async"
        />
      </aside>
    </div>
  )
}
